import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDisplayWebContent } from '../hooks/useDisplayWebContent';
import { SegmentationModal, SegmentationRule } from '@salecycle/experimental/Modal/SegmentationModal';
import { ConfigurationOption } from '../../components/ConfigurationOption';
import { SelectSegmentationButton } from '../../components/SelectSegmentationButton';
import { OnsiteSegmentationOptions } from 'models/segmentation/OnsiteSegmentationOptionsFactory';
import { SelectedSegmentationRules } from '@salecycle/experimental/Segmentation/SelectedSegmentationRules';

const SelectedRules = ({ id }) => {
  const { t } = useTranslation('campaigns');
  const { getSegmentation } = useDisplayWebContent();

  const segmentation = useMemo(() => getSegmentation(id), [getSegmentation, id]);

  return <SelectedSegmentationRules rules={segmentation} t={t} useMargins />;
};

export const DisplaySegmentationConfiguration = ({ id }) => {
  const { t } = useTranslation('campaigns');
  const { getSegmentation, setSegmentationChosen, saveSegmentation } = useDisplayWebContent();

  const segmentation = useMemo(() => getSegmentation(id), [getSegmentation, id]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openModal = useCallback(() => setIsModalOpen(true), []);

  const onSegmentationSaved = useCallback(
    (segmentation: SegmentationRule<any, any, any>[][]) => {
      if (segmentation.length) {
        saveSegmentation(id, segmentation);
      } else {
        // This sets the segmentation as an empty array. Ideally we'd have a more appropriately named action.
        setSegmentationChosen(id);
      }
    },
    [id, saveSegmentation, setSegmentationChosen],
  );

  const factory = useMemo(() => new OnsiteSegmentationOptions(t), [t]);

  return (
    <>
      <ConfigurationOption
        title={'Audience segmentation '}
        optional={'(optional)'}
        aria-label={t('onsite.editor.workflow.displayWebContentMessage.drawer.segmentation.displayConfigAriaLabel')}
      >
        <SelectedRules id={id} />
        <SelectSegmentationButton segmentation={segmentation} openModal={openModal} />
      </ConfigurationOption>

      <SegmentationModal
        open={isModalOpen}
        onClose={closeModal}
        onSave={onSegmentationSaved}
        initialRules={segmentation}
        optionsFactory={factory}
        hasOROption
      />
    </>
  );
};
